import {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ActivityIndicator,
  AppState,
  Dimensions,
  LayoutAnimation,
  type ListRenderItemInfo,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native'
import {useSharedValue} from 'react-native-reanimated'
import {type RichText as RichTextType} from '@bsky/sdk/richtext'
import {useLingui} from '@lingui/react/macro'
import {useQueryClient} from '@tanstack/react-query'

import {DISCOVER_FEED_URI, KNOWN_SHUTDOWN_FEEDS} from '#/lib/constants'
import {useBottomBarOffset} from '#/lib/hooks/useBottomBarOffset'
import {useInitialNumToRender} from '#/lib/hooks/useInitialNumToRender'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {ScrollProvider} from '#/lib/ScrollContext'
import {cleanError, isNetworkError} from '#/lib/strings/errors'
import {logger} from '#/logger'
import {usePostAuthorShadowFilter} from '#/state/cache/profile-shadow'
import {listenPostCreated} from '#/state/events'
import {useFeedFeedbackContext} from '#/state/feed-feedback'
import {useTrendingSettings} from '#/state/preferences/trending'
import {STALE} from '#/state/queries'
import {truncateAndInvalidate} from '#/state/queries/util'
import {useSession} from '#/state/session'
import {useProgressGuide} from '#/state/shell/progress-guide'
import {useSelectedFeed} from '#/state/shell/selected-feed'
import {ComposerPrompt} from '#/view/com/feeds/ComposerPrompt'
import {DiscoverFallbackHeader} from '#/view/com/posts/DiscoverFallbackHeader'
import {FeedShutdownMsg} from '#/view/com/posts/FeedShutdownMsg'
import {PostFeedErrorMessage} from '#/view/com/posts/PostFeedErrorMessage'
import {PostFeedItem} from '#/view/com/posts/PostFeedItem'
import {ShowLessFollowup} from '#/view/com/posts/ShowLessFollowup'
import {ViewFullThread} from '#/view/com/posts/ViewFullThread'
import {ErrorMessage} from '#/view/com/util/error/ErrorMessage'
import {List, type ListRef} from '#/view/com/util/List'
import {LoadMoreRetryBtn} from '#/view/com/util/LoadMoreRetryBtn'
import {type VideoFeedSourceContext} from '#/screens/VideoFeed/types'
import {atoms as a, useBreakpoints, useLayoutBreakpoints, useTheme} from '#/alf'
import {
  AgeAssuranceDismissibleFeedBanner,
  useInternalState as useAgeAssuranceBannerState,
} from '#/components/ageAssurance/AgeAssuranceDismissibleFeedBanner'
import {ProgressGuide, SuggestedFollows} from '#/components/FeedInterstitials'
import {
  PostFeedVideoGridRow,
  PostFeedVideoGridRowPlaceholder,
} from '#/components/feeds/PostFeedVideoGridRow'
import {FeedTrendingTopicsInterstitial} from '#/components/interstitials/FeedTrendingTopics'
import {TrendingVideos as TrendingVideosInterstitial} from '#/components/interstitials/TrendingVideos'
import {isStandardSiteEmbed} from '#/components/Post/Embed/StandardSiteEmbed/utils'
import {PostFeedSkeleton} from '#/components/PostFeedSkeleton'
import {RichText} from '#/components/RichText'
import {useAnalytics} from '#/analytics'
import {IS_IOS, IS_NATIVE, IS_WEB} from '#/env'
import {isFollowingV2Eligible} from '#/features/followingV2/eligibility'
import {DiscoverFeedLiveEventFeedsAndTrendingBanner} from '#/features/liveEvents/components/DiscoverFeedLiveEventFeedsAndTrendingBanner'
import {
  isStatusStillActive,
  isStatusValidForViewers,
  useLiveNowConfig,
} from '#/features/liveNow'
import {app} from '#/lexicons'
import * as bsky from '#/types/bsky'
import {GapRow} from './GapRow'
import {mixSamples} from './mixSamples'
import {PrependPill} from './PrependPill'
import {
  type AuthorFilter,
  type FeedDescriptor,
  type FeedParams,
  type FeedPostSlice,
  type FeedPostSliceItem,
  findGaps,
  hasUnseenPosts,
  pollLatest,
  RQKEY,
  usePostFeedFetcher,
  usePostFeedGapFill,
  usePostFeedPrepend,
  usePostFeedQuery,
  usePostFeedRefresh,
} from './queries/postFeed'
import {useSavedFeedSamples} from './queries/savedFeedSamples'
import {useAnchorCorrectionScrollHandlers} from './useAnchorCorrectionScrollHandlers'
import {useListRest} from './useListRest'
import {useNewPostsCheck} from './useNewPostsCheck'
import {usePrependPill} from './usePrependPill'
import {useSettleAtTop} from './useSettleAtTop'
import {useSettleScrollHandlers} from './useSettleScrollHandlers'

type FeedRow =
  | {
      type: 'loading'
      key: string
    }
  | {
      type: 'empty'
      key: string
    }
  | {
      type: 'error'
      key: string
    }
  | {
      type: 'loadMoreError'
      key: string
    }
  | {
      type: 'refreshError'
      key: string
      error: Error
    }
  | {
      type: 'feedShutdownMsg'
      key: string
    }
  | {
      type: 'fallbackMarker'
      key: string
    }
  | {
      type: 'description'
      key: string
      value: RichTextType
    }
  | {
      type: 'sliceItem'
      key: string
      slice: FeedPostSlice
      indexInSlice: number
      showReplyTo: boolean
    }
  | {
      type: 'videoGridRowPlaceholder'
      key: string
    }
  | {
      type: 'videoGridRow'
      key: string
      items: FeedPostSliceItem[]
      sourceFeedUri: string
      feedContexts: (string | undefined)[]
      reqIds: (string | undefined)[]
    }
  | {
      type: 'sliceViewFullThread'
      key: string
      uri: string
    }
  | {
      type: 'interstitialFollows'
      key: string
    }
  | {
      type: 'interstitialProgressGuide'
      key: string
    }
  | {
      type: 'interstitialFeedTrendingTopics'
      key: string
      feedSliceIndex: number
    }
  | {
      type: 'interstitialTrendingVideos'
      key: string
    }
  | {
      type: 'showLessFollowup'
      key: string
    }
  | {
      type: 'ageAssuranceBanner'
      key: string
    }
  | {
      type: 'composerPrompt'
      key: string
    }
  | {
      type: 'liveEventFeedsAndTrendingBanner'
      key: string
    }
  | {
      type: 'gap'
      key: string
      /** The cursor of the page above the gap. */
      cursor: string
      isOpen: boolean
    }

export function getItemsForFeedback(feedRow: FeedRow): {
  item: FeedPostSliceItem
  feedContext: string | undefined
  reqId: string | undefined
}[] {
  if (feedRow.type === 'sliceItem') {
    return feedRow.slice.items.map(item => ({
      item,
      feedContext: feedRow.slice.feedContext,
      reqId: feedRow.slice.reqId,
    }))
  } else if (feedRow.type === 'videoGridRow') {
    return feedRow.items.map((item, i) => ({
      item,
      feedContext: feedRow.feedContexts[i],
      reqId: feedRow.reqIds[i],
    }))
  } else {
    return []
  }
}

export type PostFeedRef = {
  /** Refreshes the feed in one write. Callers log their own metric. */
  refresh: () => Promise<void>
}

// DISABLED need to check if this is causing random feed refreshes -prf
// const REFRESH_AFTER = STALE.HOURS.ONE
const CHECK_LATEST_AFTER = STALE.SECONDS.THIRTY

/**
 * Rows that show posts. The rows above the first of them are headers, which
 * `maintainVisibleContentPosition` mustn't anchor on.
 */
const POST_ROW_TYPES: ReadonlySet<FeedRow['type']> = new Set([
  'sliceItem',
  'sliceViewFullThread',
  'showLessFollowup',
  'videoGridRow',
  'fallbackMarker',
  'gap',
])

let PostFeed = ({
  feed,
  description,
  feedParams,
  ignoreFilterFor,
  style,
  enabled,
  isActive = false,
  pollInterval,
  disablePoll,
  scrollElRef,
  onScrolledDownChange,
  onHasNew,
  renderEmptyState,
  renderEndOfFeed,
  testID,
  headerOffset = 0,
  progressViewOffset,
  desktopFixedHeightOffset,
  ListHeaderComponent,
  extraData,
  savedFeedConfig,
  initialNumToRender: initialNumToRenderOverride,
  isVideoFeed = false,
  ref,
}: {
  feed: FeedDescriptor
  description?: RichTextType
  feedParams?: FeedParams
  ignoreFilterFor?: string
  style?: StyleProp<ViewStyle>
  enabled?: boolean
  /**
   * Whether this is the feed on screen: the focused page of a focused Home.
   * Only then does anchored Following check for new posts, and offer the
   * posts it put on top with the pill.
   */
  isActive?: boolean
  pollInterval?: number
  disablePoll?: boolean
  scrollElRef?: ListRef
  onHasNew?: (v: boolean) => void
  onScrolledDownChange?: (isScrolledDown: boolean) => void
  renderEmptyState: () => React.ReactElement
  renderEndOfFeed?: () => React.ReactElement
  testID?: string
  headerOffset?: number
  progressViewOffset?: number
  desktopFixedHeightOffset?: number
  ListHeaderComponent?: () => React.ReactElement
  extraData?: Record<string, unknown>
  savedFeedConfig?: app.bsky.actor.defs.SavedFeed
  initialNumToRender?: number
  isVideoFeed?: boolean
  lastFetchDate?: () => number
  ref?: React.Ref<PostFeedRef>
}): React.ReactNode => {
  const ax = useAnalytics()
  const t = useTheme()
  const {t: l} = useLingui()
  const queryClient = useQueryClient()
  const {currentAccount, hasSession} = useSession()
  const initialNumToRender = useInitialNumToRender()
  const feedFeedback = useFeedFeedbackContext()
  const [isPTRing, setIsPTRing] = useState(false)
  // eslint-disable-next-line react-hooks/purity
  const lastFetchRef = useRef<number>(Date.now())
  const [feedType, feedUriOrActorDid, feedTab] = feed.split('|')
  const {gtMobile} = useBreakpoints()
  const {rightNavVisible} = useLayoutBreakpoints()
  const areVideoFeedsEnabled = IS_NATIVE

  const trendingIndices = ax.features.getValue(
    ax.features.TrendingDiscoverValues,
    {
      topics: 5,
      accounts: 15,
      videos: 30,
    },
  )

  const [hasPressedShowLessUris, setHasPressedShowLessUris] = useState(
    () => new Set<string>(),
  )
  const onPressShowLess = useCallback(
    (interaction: app.bsky.feed.defs.Interaction) => {
      if (interaction.item) {
        const uri = interaction.item
        setHasPressedShowLessUris(prev => new Set([...prev, uri]))
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
      }
    },
    [],
  )

  const feedCacheKey = feedParams?.feedCacheKey
  const opts = useMemo(
    () => ({enabled, ignoreFilterFor}),
    [enabled, ignoreFilterFor],
  )
  const {
    data: feedData,
    isFetching,
    isFetched,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = usePostFeedQuery(feed, feedParams, opts)
  const {createFeedApi} = usePostFeedFetcher(feed)
  const {
    refresh,
    error: refreshError,
    isRefreshing,
    isPending: isRefreshPending,
  } = usePostFeedRefresh(feed, feedParams)
  const samples = useSavedFeedSamples({
    enabled: enabled !== false && feed === 'following',
    pages: feedData?.pages,
  })
  const data =
    feedData && samples
      ? {...feedData, pages: mixSamples(feedData.pages, samples)}
      : feedData
  const lastFetchedAt = data?.pages[0].fetchedAt
  const isEmpty = useMemo(
    () => !isFetching && !data?.pages?.some(page => page.slices.length),
    [isFetching, data],
  )

  /**
   * Whether the list holds on to the row the reader is on when posts are put
   * above it, as Following v2 does on Home's Following feed, on native. The
   * rows that would sit above the posts are left out, as they'd take the
   * anchor from them.
   */
  const isAnchored = feed === 'following' && isFollowingV2Eligible(ax)
  /**
   * When the list is at rest, for prepends to wait for. Its scroll handlers
   * take the corrections anchoring makes to the offset out of what the Home
   * header sees, so they can't hide or show it.
   */
  const listRest = useListRest(
    useAnchorCorrectionScrollHandlers(isAnchored),
    isAnchored,
  )
  const prepend = usePostFeedPrepend(feed, feedParams, {
    enabled: isAnchored && enabled !== false,
    topFetchedAt: lastFetchedAt,
    listAtRest: listRest.atRest,
    isRefreshing: isRefreshPending,
  })
  const fillGap = usePostFeedGapFill(feed, feedParams)
  /*
   * Pressing "Show more posts" below the posts staged on top, the reader has
   * reached them.
   */
  const onFillGap = useNonReactiveCallback((cursor: string) => {
    if (cursor === feedData?.pages[0]?.cursor) {
      prepend.markRead()
    }
    return fillGap(cursor)
  })
  const settleAtTop = useSettleAtTop(feed, feedParams, {
    enabled: isAnchored && enabled !== false,
    // At the newest post, nothing is new to the reader any more.
    onRestAtTop: () => onHasNew?.(false),
  })
  /** The list's scroll offset, which its scroll handlers keep. */
  const listOffsetY = useSharedValue(0)

  /**
   * The top page a refresh from this view wrote, to take the reader up to once
   * it has rendered: anchored, the list would otherwise hold on to the row
   * they're on, below the new posts. Not animated, as that can be a long way.
   */
  const revealTopRef = useRef<number>(undefined)
  const revealTop = useNonReactiveCallback(() => {
    scrollElRef?.current?.scrollToOffset({
      animated: false,
      offset: -headerOffset,
    })
  })
  const refreshToTop = async () => {
    const page = await refresh()
    if (!page || !isAnchored) {
      return
    }
    if (lastFetchRef.current === page.fetchedAt) {
      revealTop()
    } else {
      revealTopRef.current = page.fetchedAt
    }
  }

  useEffect(() => {
    if (lastFetchedAt) {
      lastFetchRef.current = lastFetchedAt
      if (lastFetchedAt === revealTopRef.current) {
        revealTopRef.current = undefined
        revealTop()
      }
    }
  }, [lastFetchedAt, revealTop])

  const checkForNew = useNonReactiveCallback(async () => {
    if (
      !data?.pages[0] ||
      isFetching ||
      isRefreshing ||
      // Anchored Following checks with useNewPostsCheck instead, below.
      isAnchored ||
      !onHasNew ||
      !enabled ||
      disablePoll
    ) {
      return
    }

    // Discover always has fresh content
    if (feedUriOrActorDid === DISCOVER_FEED_URI) {
      return onHasNew(true)
    }

    try {
      if (await pollLatest(data.pages[0], createFeedApi())) {
        if (isEmpty) {
          void refreshToTop()
        } else {
          onHasNew(true)
        }
      }
    } catch (e) {
      if (!isNetworkError(e)) {
        logger.warn('Poll latest failed', {feed, message: String(e)})
      }
    }
  })

  /*
   * Anchored Following's checks, made by the view on screen only. Each one
   * that finds newer posts stages them above the reader at rest (see
   * `usePostFeedPrepend`) and lights the Home dot. Only a return to the app
   * offers them with the pill. With no boundary to fetch above, as for an
   * empty feed, it peeks for the Home dot instead, as other feeds do.
   */
  useNewPostsCheck({
    topFetchedAt: lastFetchedAt,
    isEmpty,
    isActive: isAnchored && isActive,
    // A refetch from the top is about to show what a check would find.
    isBusy: isRefreshing || (isFetching && !isFetchingNextPage),
    /*
     * Not paused while the Home dot is lit, as `disablePoll` is, since each
     * check replaces what's staged with the newest posts.
     */
    interval: pollInterval,
    check: async trigger => {
      const staging = prepend.check(trigger)
      if (!staging) {
        return pollLatest(feedData?.pages[0], createFeedApi())
      }
      const page = await staging
      if (page && hasUnseenPosts(feedData?.pages[0], page.feed)) {
        onHasNew?.(true)
      }
      return undefined
    },
    onFound: () => {
      if (isEmpty) {
        void refreshToTop()
      } else {
        onHasNew?.(true)
      }
    },
  })

  const isScrolledDownRef = useRef(false)
  const handleScrolledDownChange = (isScrolledDown: boolean) => {
    isScrolledDownRef.current = isScrolledDown
    onScrolledDownChange?.(isScrolledDown)
  }

  const myDid = currentAccount?.did || ''
  const onPostCreated = useCallback(() => {
    // NOTE
    // only invalidate if at the top of the feed
    // changing content when scrolled can trigger some UI freakouts on iOS and android
    // -sfn
    if (
      !isScrolledDownRef.current &&
      (feed === 'following' ||
        feed === `author|${myDid}|posts_and_author_threads`)
    ) {
      /*
       * A disabled feed isn't shown, so it can refetch once it's enabled.
       * Truncating first means that refetch is one page, not every page it
       * had loaded.
       */
      if (enabled) {
        void refreshToTop()
      } else {
        void truncateAndInvalidate(queryClient, RQKEY(feed))
      }
    }
  }, [queryClient, feed, myDid, enabled, refreshToTop])
  useEffect(() => {
    return listenPostCreated(onPostCreated)
  }, [onPostCreated])

  useEffect(() => {
    if (enabled && !disablePoll) {
      const timeSinceFirstLoad = Date.now() - lastFetchRef.current
      if (isEmpty || timeSinceFirstLoad > CHECK_LATEST_AFTER) {
        // check for new on enable (aka on focus)
        void checkForNew()
      }
    }
  }, [enabled, isEmpty, disablePoll, checkForNew])

  useEffect(() => {
    let cleanup1: () => void | undefined, cleanup2: () => void | undefined
    const subscription = AppState.addEventListener('change', nextAppState => {
      // check for new on app foreground
      if (nextAppState === 'active') {
        void checkForNew()
      }
    })
    cleanup1 = () => subscription.remove()
    if (pollInterval) {
      // check for new on interval
      const i = setInterval(() => {
        void checkForNew()
      }, pollInterval)
      cleanup2 = () => clearInterval(i)
    }
    return () => {
      cleanup1?.()
      cleanup2?.()
    }
  }, [pollInterval, checkForNew])

  const followProgressGuide = useProgressGuide('follow-10')
  const followAndLikeProgressGuide = useProgressGuide('like-10-and-follow-7')

  const showProgressInterstitial =
    (followProgressGuide || followAndLikeProgressGuide) && !rightNavVisible

  const {trendingVideoDisabled} = useTrendingSettings()

  const ageAssuranceBannerState = useAgeAssuranceBannerState()
  const selectedFeed = useSelectedFeed()
  /**
   * Cached value of whether the current feed was selected at startup. We don't
   * want this to update when user swipes.
   */
  // oxlint-disable-next-line react/hook-use-state
  const [isCurrentFeedAtStartupSelected] = useState(selectedFeed === feed)

  const blockedOrMutedAuthors = usePostAuthorShadowFilter(
    // author feeds have their own handling
    feed.startsWith('author|') ? undefined : data?.pages,
  )

  /**
   * A refresh leaves the query's own error in place, so while one retries an
   * errored empty feed, the loading row stands in for the error row.
   */
  const isRetryingError = isRefreshing && isError && isEmpty

  const feedItems: FeedRow[] = useMemo(() => {
    // wraps a slice item, and replaces it with a showLessFollowup item
    // if the user has pressed show less on it
    const sliceItem = (row: Extract<FeedRow, {type: 'sliceItem'}>) => {
      if (hasPressedShowLessUris.has(row.slice.items[row.indexInSlice]?.uri)) {
        return {
          type: 'showLessFollowup',
          key: row.key,
        } as const
      } else {
        return row
      }
    }

    let feedKind: 'following' | 'discover' | 'profile' | 'thevids' | undefined
    if (feedType === 'following') {
      feedKind = 'following'
    } else if (feedUriOrActorDid === DISCOVER_FEED_URI) {
      feedKind = 'discover'
    } else if (
      feedType === 'author' &&
      (feedTab === 'posts_and_author_threads' ||
        feedTab === 'posts_with_replies')
    ) {
      feedKind = 'profile'
    }

    let arr: FeedRow[] = []
    if (KNOWN_SHUTDOWN_FEEDS.includes(feedUriOrActorDid)) {
      arr.push({
        type: 'feedShutdownMsg',
        key: 'feedShutdownMsg',
      })
    }
    /*
     * Data restored from disk can be in the cache, fetched, before what
     * moderates it is ready, which the query's `select` waits for. It loads
     * until then.
     */
    if (isFetched && !isRetryingError && (data || isError)) {
      if (isError && isEmpty) {
        arr.push({
          type: 'error',
          key: 'error',
        })
      } else if (isEmpty) {
        arr.push({
          type: 'empty',
          key: 'empty',
        })
      } else if (data) {
        let sliceIndex = -1

        if (isVideoFeed) {
          const videos: {
            item: FeedPostSliceItem
            feedContext: string | undefined
            reqId: string | undefined
          }[] = []
          for (const page of data.pages) {
            for (const slice of page.slices) {
              const item = slice.items.find(
                item => item.uri === slice.feedPostUri,
              )
              if (
                item &&
                bsky.isType(app.bsky.embed.video.view, item.post.embed) &&
                !blockedOrMutedAuthors.includes(item.post.author.did)
              ) {
                videos.push({
                  item,
                  feedContext: slice.feedContext,
                  reqId: slice.reqId,
                })
              }
            }
          }

          const rows: {
            item: FeedPostSliceItem
            feedContext: string | undefined
            reqId: string | undefined
          }[][] = []
          for (let i = 0; i < videos.length; i++) {
            const video = videos[i]
            const item = video.item
            const cols = gtMobile ? 3 : 2
            const rowItem = {
              item,
              feedContext: video.feedContext,
              reqId: video.reqId,
            }
            if (i % cols === 0) {
              rows.push([rowItem])
            } else {
              rows[rows.length - 1].push(rowItem)
            }
          }

          for (const row of rows) {
            sliceIndex++
            arr.push({
              type: 'videoGridRow',
              key: row.map(r => r.item._reactKey).join('-'),
              items: row.map(r => r.item),
              sourceFeedUri: feedUriOrActorDid,
              feedContexts: row.map(r => r.feedContext),
              reqIds: row.map(r => r.reqId),
            })
          }
        } else {
          // Only anchored Following runs the restore prepend that leaves gaps.
          const gaps = isAnchored ? findGaps(data.pages) : undefined
          for (const [pageIndex, page] of data.pages.entries()) {
            for (const slice of page.slices) {
              sliceIndex++

              if (hasSession) {
                if (feedKind === 'discover') {
                  if (sliceIndex === 0) {
                    if (showProgressInterstitial) {
                      arr.push({
                        type: 'interstitialProgressGuide',
                        key: 'interstitial-' + sliceIndex + '-' + lastFetchedAt,
                      })
                    } else {
                      /*
                       * Only insert if Discover was the last selected feed at
                       * startup, the progress guide isn't shown, and the
                       * banner is eligible to be shown.
                       */
                      if (
                        isCurrentFeedAtStartupSelected &&
                        ageAssuranceBannerState.visible
                      ) {
                        arr.push({
                          type: 'ageAssuranceBanner',
                          key: 'ageAssuranceBanner-' + sliceIndex,
                        })
                      }
                    }
                    arr.push({
                      type: 'liveEventFeedsAndTrendingBanner',
                      key: 'liveEventFeedsAndTrendingBanner-' + sliceIndex,
                    })
                    // Show composer prompt for Discover and Following feeds
                    if (
                      hasSession &&
                      (feedUriOrActorDid === DISCOVER_FEED_URI ||
                        feed === 'following')
                    ) {
                      arr.push({
                        type: 'composerPrompt',
                        key: 'composerPrompt-' + sliceIndex,
                      })
                    }
                  } else if (sliceIndex === trendingIndices.topics) {
                    arr.push({
                      type: 'interstitialFeedTrendingTopics',
                      key: 'interstitialFeedTrendingTopics-' + sliceIndex,
                      feedSliceIndex: sliceIndex,
                    })
                  } else if (sliceIndex === trendingIndices.videos) {
                    if (areVideoFeedsEnabled && !trendingVideoDisabled) {
                      arr.push({
                        type: 'interstitialTrendingVideos',
                        key: 'interstitial-' + sliceIndex + '-' + lastFetchedAt,
                      })
                    }
                  } else if (sliceIndex === trendingIndices.accounts) {
                    arr.push({
                      type: 'interstitialFollows',
                      key: 'interstitial-' + sliceIndex + '-' + lastFetchedAt,
                    })
                  }
                } else if (feedKind === 'following') {
                  if (sliceIndex === 0) {
                    // Show composer prompt for Following feed, unless anchored
                    if (hasSession && !isAnchored) {
                      arr.push({
                        type: 'composerPrompt',
                        key: 'composerPrompt-' + sliceIndex,
                      })
                    }
                  }
                } else if (feedKind === 'profile') {
                  if (sliceIndex === 5) {
                    arr.push({
                      type: 'interstitialFollows',
                      key: 'interstitial-' + sliceIndex + '-' + lastFetchedAt,
                    })
                  }
                } else {
                  /*
                   * Only insert if this feed was the last selected feed at
                   * startup and the banner is eligible to be shown.
                   */
                  if (sliceIndex === 0 && isCurrentFeedAtStartupSelected) {
                    arr.push({
                      type: 'ageAssuranceBanner',
                      key: 'ageAssuranceBanner-' + sliceIndex,
                    })
                  }
                }
              }

              if (slice.isFallbackMarker) {
                arr.push({
                  type: 'fallbackMarker',
                  key:
                    'sliceFallbackMarker-' + sliceIndex + '-' + lastFetchedAt,
                })
              } else if (
                slice.items.some(item =>
                  blockedOrMutedAuthors.includes(item.post.author.did),
                )
              ) {
                // skip
              } else if (slice.isIncompleteThread && slice.items.length >= 3) {
                const beforeLast = slice.items.length - 2
                const last = slice.items.length - 1
                arr.push(
                  sliceItem({
                    type: 'sliceItem',
                    key: slice.items[0]._reactKey,
                    slice: slice,
                    indexInSlice: 0,
                    showReplyTo: false,
                  }),
                )
                arr.push({
                  type: 'sliceViewFullThread',
                  key: slice._reactKey + '-viewFullThread',
                  uri: slice.items[0].uri,
                })
                arr.push(
                  sliceItem({
                    type: 'sliceItem',
                    key: slice.items[beforeLast]._reactKey,
                    slice: slice,
                    indexInSlice: beforeLast,
                    showReplyTo:
                      slice.items[beforeLast].parentAuthor?.did !==
                      slice.items[beforeLast].post.author.did,
                  }),
                )
                arr.push(
                  sliceItem({
                    type: 'sliceItem',
                    key: slice.items[last]._reactKey,
                    slice: slice,
                    indexInSlice: last,
                    showReplyTo: false,
                  }),
                )
              } else {
                for (let i = 0; i < slice.items.length; i++) {
                  arr.push(
                    sliceItem({
                      type: 'sliceItem',
                      key: slice.items[i]._reactKey,
                      slice: slice,
                      indexInSlice: i,
                      showReplyTo: i === 0,
                    }),
                  )
                }
              }
            }
            const gap = gaps?.get(pageIndex)
            if (gap && page.cursor) {
              // The same key once it's filled, so the row stays.
              arr.push({
                type: 'gap',
                key: 'gap-' + page.cursor,
                cursor: page.cursor,
                isOpen: gap === 'open',
              })
            }
          }
        }
      }
      if (isError && !isEmpty) {
        arr.push({
          type: 'loadMoreError',
          key: 'loadMoreError',
        })
      }
    } else {
      if (isVideoFeed) {
        arr.push({
          type: 'videoGridRowPlaceholder',
          key: 'videoGridRowPlaceholder',
        })
      } else {
        arr.push({
          type: 'loading',
          key: 'loading',
        })
      }
    }

    if (description?.text) {
      arr.unshift({
        key: 'description',
        type: 'description',
        value: description,
      })
    }

    /*
     * A failed refresh keeps the posts it failed to replace, so its error goes
     * above them. An errored empty feed shows it in its error row instead.
     */
    if (refreshError && !(isError && isEmpty)) {
      arr.unshift({
        type: 'refreshError',
        key: 'refreshError',
        error: refreshError,
      })
    }

    return arr
  }, [
    isAnchored,
    refreshError,
    isRetryingError,
    description,
    isFetched,
    isError,
    isEmpty,
    lastFetchedAt,
    data,
    feed,
    feedType,
    feedUriOrActorDid,
    feedTab,
    hasSession,
    showProgressInterstitial,
    trendingVideoDisabled,
    gtMobile,
    isVideoFeed,
    areVideoFeedsEnabled,
    hasPressedShowLessUris,
    ageAssuranceBannerState,
    isCurrentFeedAtStartupSelected,
    blockedOrMutedAuthors,
    trendingIndices,
  ])

  const pill = usePrependPill({
    enabled: isAnchored,
    isActive,
    staged: prepend.staged,
    rows: feedItems,
    // Without samples, which the pill never offers.
    pages: feedData?.pages,
    offsetY: listOffsetY,
    scrollToTop: () => {
      scrollElRef?.current?.scrollToOffset({
        animated: true,
        offset: -headerOffset,
      })
    },
    onRead: prepend.markRead,
  })
  /** Tells the prepend the reader saw a row, if it's from the top page. */
  const onTopRowSeen = useNonReactiveCallback((row: FeedRow) => {
    const top = data?.pages[0]
    if (
      prepend.staged &&
      top &&
      ((row.type === 'sliceItem' && top.slices.includes(row.slice)) ||
        (row.type === 'gap' && row.cursor === top.cursor))
    ) {
      prepend.onPageSeen(top.fetchedAt)
    }
  })
  /**
   * The list's scroll handlers. Settling, staging and the pill judge the
   * offset as the list reports it, as they have to know where the list really
   * is. The Home
   * header sees it with the corrections anchoring makes taken out, so they
   * can't hide or show it.
   */
  const scrollHandlers = useSettleScrollHandlers(
    listRest.scrollHandlers,
    isAnchored
      ? {
          ...settleAtTop,
          onBeginDrag: () => {
            settleAtTop.onBeginDrag()
            prepend.onBeginDrag()
          },
          // At the top, the reader has passed everything staged above them.
          onReachTop: prepend.markRead,
        }
      : undefined,
    listOffsetY,
  )

  // events
  // =
  //

  const onRefresh = async () => {
    setIsPTRing(true)
    if (enabled) {
      ax.metric('feed:refresh', {
        feedType: feedType,
        feedUrl: feed,
        reason: 'pull-to-refresh',
      })
      await refreshToTop()
      onHasNew?.(false)
    }
    setIsPTRing(false)
  }

  useImperativeHandle(ref, () => ({refresh: refreshToTop}))

  const onEndReached = useCallback(async () => {
    if (isFetching || !hasNextPage || isError) return

    ax.metric('feed:endReached', {
      feedType: feedType,
      feedUrl: feed,
      itemCount: feedItems.length,
    })
    try {
      await fetchNextPage()
    } catch (err) {
      logger.error('Failed to load more posts', {message: err})
    }
  }, [
    ax,
    isFetching,
    hasNextPage,
    isError,
    fetchNextPage,
    feed,
    feedType,
    feedItems.length,
  ])

  const onPressTryAgain = useCallback(() => {
    void refreshToTop()
    onHasNew?.(false)
  }, [refreshToTop, onHasNew])

  const onPressRetryLoadMore = useCallback(() => {
    void fetchNextPage()
  }, [fetchNextPage])

  // rendering
  // =

  const renderItem = useCallback(
    ({item: row, index: rowIndex}: ListRenderItemInfo<FeedRow>) => {
      if (row.type === 'empty') {
        return renderEmptyState()
      } else if (row.type === 'error') {
        return (
          <PostFeedErrorMessage
            feedDesc={feed}
            error={refreshError ?? error ?? undefined}
            onPressTryAgain={onPressTryAgain}
            savedFeedConfig={savedFeedConfig}
          />
        )
      } else if (row.type === 'refreshError') {
        return (
          <ErrorMessage
            message={cleanError(row.error)}
            onPressTryAgain={onPressTryAgain}
          />
        )
      } else if (row.type === 'loadMoreError') {
        return (
          <LoadMoreRetryBtn
            label={l`There was an issue fetching posts. Tap here to try again.`}
            onPress={onPressRetryLoadMore}
          />
        )
      } else if (row.type === 'loading') {
        return <PostFeedSkeleton />
      } else if (row.type === 'feedShutdownMsg') {
        return <FeedShutdownMsg feedUri={feedUriOrActorDid} />
      } else if (row.type === 'description') {
        return (
          <RichText
            value={row.value}
            style={[
              a.m_md,
              a.text_md,
              a.leading_snug,
              t.atoms.text_contrast_high,
            ]}
          />
        )
      } else if (row.type === 'interstitialFollows') {
        return <SuggestedFollows feed={feed} />
      } else if (row.type === 'interstitialProgressGuide') {
        return <ProgressGuide />
      } else if (row.type === 'ageAssuranceBanner') {
        return <AgeAssuranceDismissibleFeedBanner />
      } else if (row.type === 'interstitialFeedTrendingTopics') {
        return (
          <FeedTrendingTopicsInterstitial feedSliceIndex={row.feedSliceIndex} />
        )
      } else if (row.type === 'liveEventFeedsAndTrendingBanner') {
        return <DiscoverFeedLiveEventFeedsAndTrendingBanner />
      } else if (row.type === 'composerPrompt') {
        return <ComposerPrompt />
      } else if (row.type === 'interstitialTrendingVideos') {
        return <TrendingVideosInterstitial />
      } else if (row.type === 'fallbackMarker') {
        // HACK
        // tell the user we fell back to discover
        // see home.ts (feed api) for more info
        // -prf
        return <DiscoverFallbackHeader />
      } else if (row.type === 'sliceItem') {
        const slice = row.slice
        const indexInSlice = row.indexInSlice
        const item = slice.items[indexInSlice]
        return (
          <PostFeedItem
            post={item.post}
            record={item.record}
            postNumbering={item.postNumbering}
            reason={indexInSlice === 0 ? slice.reason : undefined}
            feedContext={slice.feedContext}
            reqId={slice.reqId}
            moderation={item.moderation}
            parentAuthor={item.parentAuthor}
            showReplyTo={row.showReplyTo}
            isThreadParent={isThreadParentAt(slice.items, indexInSlice)}
            isThreadChild={isThreadChildAt(slice.items, indexInSlice)}
            isThreadLastChild={
              isThreadChildAt(slice.items, indexInSlice) &&
              slice.items.length === indexInSlice + 1
            }
            isParentBlocked={item.isParentBlocked}
            isParentNotFound={item.isParentNotFound}
            hideTopBorder={rowIndex === 0 && indexInSlice === 0}
            rootPost={slice.items[0].post}
            onShowLess={onPressShowLess}
          />
        )
      } else if (row.type === 'sliceViewFullThread') {
        return <ViewFullThread uri={row.uri} />
      } else if (row.type === 'videoGridRowPlaceholder') {
        return (
          <View>
            <PostFeedVideoGridRowPlaceholder />
            <PostFeedVideoGridRowPlaceholder />
            <PostFeedVideoGridRowPlaceholder />
          </View>
        )
      } else if (row.type === 'videoGridRow') {
        let sourceContext: VideoFeedSourceContext
        if (feedType === 'author') {
          sourceContext = {
            type: 'author',
            did: feedUriOrActorDid,
            filter: feedTab as AuthorFilter,
          }
        } else {
          sourceContext = {
            type: 'feedgen',
            uri: row.sourceFeedUri,
            sourceInterstitial: feedCacheKey ?? 'none',
            followingV2: true,
          }
        }

        return (
          <PostFeedVideoGridRow
            items={row.items}
            sourceContext={sourceContext}
          />
        )
      } else if (row.type === 'showLessFollowup') {
        return <ShowLessFollowup />
      } else if (row.type === 'gap') {
        return (
          <GapRow
            isOpen={row.isOpen}
            onFill={() => onFillGap(row.cursor)}
            hideTopBorder={rowIndex === 0}
          />
        )
      } else {
        return null
      }
    },
    [
      renderEmptyState,
      feed,
      error,
      refreshError,
      onPressTryAgain,
      savedFeedConfig,
      l,
      onPressRetryLoadMore,
      feedType,
      feedUriOrActorDid,
      feedTab,
      feedCacheKey,
      onPressShowLess,
      onFillGap,
      t,
    ],
  )

  /*
   * The rows above the first post, which the anchor must skip: anchored on a
   * header row, the list holds that row in place and lets the posts below it
   * move.
   */
  let leadingRowCount = feedItems.findIndex(row => POST_ROW_TYPES.has(row.type))
  if (leadingRowCount === -1) {
    leadingRowCount = feedItems.length
  }

  const shouldRenderEndOfFeed =
    !hasNextPage && !isEmpty && !isFetching && !isError && !!renderEndOfFeed
  const bottomBarOffset = useBottomBarOffset()
  const FeedFooter = useCallback(() => {
    /*
     * A bit of padding at the bottom of the feed as you scroll and when you
     * reach the end, so that content isn't cut off by the bottom of the
     * screen. On mobile web, also clear the fixed bottom bar; on native the
     * doubled headerOffset already covers the tab bar.
     */
    const offset =
      Math.max(headerOffset, 32) * (IS_WEB ? 1 : 2) +
      (IS_WEB ? bottomBarOffset : 0)

    return isFetchingNextPage ? (
      <View style={[styles.feedFooter]}>
        <ActivityIndicator />
        <View style={{height: offset}} />
      </View>
    ) : shouldRenderEndOfFeed ? (
      <View style={{minHeight: offset}}>{renderEndOfFeed()}</View>
    ) : (
      <View style={{height: offset}} />
    )
  }, [
    isFetchingNextPage,
    shouldRenderEndOfFeed,
    renderEndOfFeed,
    headerOffset,
    bottomBarOffset,
  ])

  const liveNowConfig = useLiveNowConfig()

  const seenActorWithStatusRef = useRef<Set<string>>(new Set())
  const seenPostUrisRef = useRef<Set<string>>(new Set())
  // Tracks every post we've seen so we can fire per-post events exactly once,
  // regardless of the post's position within its slice.
  const seenPerPostUrisRef = useRef<Set<string>>(new Set())

  // Helper to calculate position in feed (count only root posts, not interstitials or thread replies)
  const getPostPosition = useNonReactiveCallback(
    (type: FeedRow['type'], key: string) => {
      // Calculate position: find the row index in feedItems, then calculate position
      const rowIndex = feedItems.findIndex(
        row => row.type === 'sliceItem' && row.key === key,
      )

      if (rowIndex >= 0) {
        let position = 0
        for (let i = 0; i < rowIndex && i < feedItems.length; i++) {
          const row = feedItems[i]
          if (row.type === 'sliceItem') {
            // Only count root posts (indexInSlice === 0), not thread replies
            if (row.indexInSlice === 0) {
              position++
            }
          } else if (row.type === 'videoGridRow') {
            // Count each video in the grid row
            position += row.items.length
          }
        }
        return position
      }
    },
  )

  const onItemSeen = useCallback(
    (item: FeedRow) => {
      feedFeedback.onItemSeen(item)
      onTopRowSeen(item)

      // Events that should fire exactly once for every new post, regardless of
      // its position within a slice or video grid row.
      const onPostSeen = (post: app.bsky.feed.defs.PostView) => {
        if (seenPerPostUrisRef.current.has(post.uri)) return
        seenPerPostUrisRef.current.add(post.uri)

        // Standard site embed view tracking
        if (
          bsky.isType(app.bsky.embed.external.view, post.embed) &&
          isStandardSiteEmbed(post.embed.external)
        ) {
          ax.metric('embed:standardSite:view', {url: post.embed.external.uri})
        }

        // Photo embed impression tracking
        if (
          bsky.isType(app.bsky.embed.images.view, post.embed) ||
          bsky.isType(app.bsky.embed.gallery.view, post.embed)
        ) {
          const totalImages = bsky.isType(
            app.bsky.embed.gallery.view,
            post.embed,
          )
            ? post.embed.items.filter(item =>
                bsky.isType(app.bsky.embed.gallery.viewImage, item),
              ).length
            : post.embed.images.length
          const useExpandedLayout = bsky.isType(
            app.bsky.embed.gallery.view,
            post.embed,
          )
            ? totalImages > 4
            : ax.features.enabled(ax.features.PostGalleryEmbedEnable)
          const layout =
            totalImages === 1
              ? 'single'
              : useExpandedLayout
                ? 'carousel'
                : 'grid'

          ax.metric('post:photoEmbed:impression', {
            layout,
            totalImages,
            postUri: post.uri,
            postAuthorDid: post.author.did,
            feedDescriptor: feedFeedback.feedDescriptor || feed,
          })
        }
      }

      // Track post:view events
      if (item.type === 'sliceItem') {
        const slice = item.slice
        const indexInSlice = item.indexInSlice
        const postItem = slice.items[indexInSlice]
        const post = postItem.post

        onPostSeen(post)

        // Track the post selected by the feed once it is actually visible.
        if (
          post.uri === slice.feedPostUri &&
          !seenPostUrisRef.current.has(post.uri)
        ) {
          seenPostUrisRef.current.add(post.uri)

          const position = getPostPosition(
            'sliceItem',
            slice.items[0]._reactKey,
          )

          ax.metric('post:view', {
            uri: post.uri,
            authorDid: post.author.did,
            isReply: !!postItem.record.reply,
            logContext: 'FeedItem',
            feedDescriptor: feedFeedback.feedDescriptor || feed,
            position,
          })
        }

        // Live status tracking (existing code)
        const actor = post.author
        if (
          actor.status &&
          isStatusValidForViewers(actor.status, liveNowConfig) &&
          isStatusStillActive(actor.status.expiresAt)
        ) {
          if (!seenActorWithStatusRef.current.has(actor.did)) {
            seenActorWithStatusRef.current.add(actor.did)
            ax.metric('live:view:post', {
              subject: actor.did,
              feed,
            })
          }
        }
      } else if (item.type === 'videoGridRow') {
        // Track each video in the grid row
        for (let i = 0; i < item.items.length; i++) {
          const postItem = item.items[i]
          const post = postItem.post

          if (!seenPostUrisRef.current.has(post.uri)) {
            seenPostUrisRef.current.add(post.uri)

            const position = getPostPosition('videoGridRow', item.key)

            ax.metric('post:view', {
              uri: post.uri,
              authorDid: post.author.did,
              isReply: !!postItem.record.reply,
              logContext: 'FeedItem',
              feedDescriptor: feedFeedback.feedDescriptor || feed,
              position,
            })
          }
        }
      }
    },
    [feedFeedback, onTopRowSeen, feed, liveNowConfig, getPostPosition, ax],
  )

  return (
    <View testID={testID} style={style}>
      <ScrollProvider {...scrollHandlers}>
        <List
          testID={testID ? `${testID}-flatlist` : undefined}
          ref={scrollElRef}
          data={feedItems}
          keyExtractor={(item: FeedRow) => item.key}
          renderItem={renderItem}
          ListFooterComponent={FeedFooter}
          ListHeaderComponent={ListHeaderComponent}
          refreshing={isPTRing}
          onRefresh={() => void onRefresh()}
          headerOffset={headerOffset}
          progressViewOffset={progressViewOffset}
          contentContainerStyle={{
            minHeight: Dimensions.get('window').height * 1.5,
          }}
          onScrolledDownChange={handleScrolledDownChange}
          onEndReached={() => void onEndReached()}
          onEndReachedThreshold={2} // number of posts left to trigger load more
          removeClippedSubviews={true}
          extraData={extraData}
          desktopFixedHeight={
            desktopFixedHeightOffset ? desktopFixedHeightOffset : true
          }
          initialNumToRender={initialNumToRenderOverride ?? initialNumToRender}
          windowSize={9}
          maxToRenderPerBatch={IS_IOS ? 5 : 1}
          updateCellsBatchingPeriod={40}
          onItemSeen={onItemSeen}
          maintainVisibleContentPosition={
            isAnchored ? {minIndexForVisible: leadingRowCount} : undefined
          }
          onLayout={isAnchored ? listRest.onLayout : undefined}
        />
      </ScrollProvider>
      {isAnchored && (
        <PrependPill
          visible={pill.visible}
          count={pill.count}
          authors={pill.authors}
          onPress={pill.onPress}
        />
      )}
    </View>
  )
}
PostFeed = memo(PostFeed)
export {PostFeed}

const styles = StyleSheet.create({
  feedFooter: {paddingTop: 20},
})

export function isThreadParentAt<T>(arr: Array<T>, i: number) {
  if (arr.length === 1) {
    return false
  }
  return i < arr.length - 1
}

export function isThreadChildAt<T>(arr: Array<T>, i: number) {
  if (arr.length === 1) {
    return false
  }
  return i > 0
}
